import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Search } from 'lucide-react';
import { useStore } from '@/lib/store';
import { useClientsConfig } from '@/config/clients-config';
import { countries } from '@/lib/countries';
import { validateVatNumber } from '@/lib/vatValidation';
import { ClientFormProps } from './types';
import { ContactPersonForm } from './ContactPersonForm';
import { ClientContactsForm } from './ClientContactsForm';
import { PEPPOL_SCHEMES, getSchemeCodeForCountry } from '@/types/peppol';
import { PeppolAPI } from '@/services/peppol';
import { 
  validateClientForm, 
  sanitizeFormData, 
  createEmptyClientForm, 
  clientToFormData,
  getFieldError,
  hasFieldError 
} from './validation';

export function ClientForm({
  client,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}: ClientFormProps) {
  const { users } = useStore();
  const config = useClientsConfig();
  
  const [formData, setFormData] = useState(createEmptyClientForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Peppol lookup state
  const [peppolLookupStatus, setPeppolLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not_found' | 'error'>('idle');
  const [peppolLookupMessage, setPeppolLookupMessage] = useState<string>('');

  // Initialize form data when client changes
  useEffect(() => {
    if (client) {
      setFormData(clientToFormData(client));
    } else {
      setFormData(createEmptyClientForm());
    }
    setErrors({});
    setPeppolLookupStatus('idle');
    setPeppolLookupMessage('');
  }, [client, isOpen]);

  // Lookup Peppol participant
  const lookupPeppolParticipant = async (participantId: string, scheme: string) => {
    if (!participantId || !scheme) return;
    
    setPeppolLookupStatus('loading');
    setPeppolLookupMessage('');
    
    try {
      const result = await PeppolAPI.lookupParticipant({
        participant_id: participantId,
        scheme: scheme,
      });
      
      if (result.exists) {
        setPeppolLookupStatus('found');
        setPeppolLookupMessage(`Found on Peppol network (${result.country_code})`);
      } else {
        setPeppolLookupStatus('not_found');
        setPeppolLookupMessage('Not registered on Peppol network');
      }
    } catch (error: any) {
      setPeppolLookupStatus('error');
      setPeppolLookupMessage(error.response?.data?.detail || 'Failed to lookup participant');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const sanitizedData = sanitizeFormData(formData);
    const validation = validateClientForm(sanitizedData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    try {
      await onSubmit(sanitizedData);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const updateFormData = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear related errors when field is updated
    if (errors && Object.keys(updates).length > 0) {
      const newErrors = { ...errors };
      Object.keys(updates).forEach(key => {
        delete newErrors[key];
      });
      setErrors(newErrors);
    }
  };

  const isFormValid = () => {
    const validation = validateClientForm(formData);
    return validation.isValid;
  };

  const getGridColumns = () => {
    if (config.formLayout === 'single-column') return 'grid-cols-1';
    if (config.formLayout === 'two-column') return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-2'; // responsive default
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {client ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogDescription>
            {client ? 'Update client details below' : 'Enter client details below'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`grid ${getGridColumns()} gap-4`}>
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Company Name {config.requiredFieldMarker}
              </Label>                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  className={hasFieldError(errors, 'name') ? 'border-red-500' : ''}
                />
              {hasFieldError(errors, 'name') && (
                <p className="text-xs text-red-500">{getFieldError(errors, 'name')}</p>
              )}
            </div>

            {/* Friendly Name */}
            {config.showFriendlyName && (
              <div className="space-y-2">
                <Label htmlFor="friendly_name">Friendly Name</Label>
                <Input
                  id="friendly_name"
                  value={formData.friendly_name}
                  onChange={(e) => updateFormData({ friendly_name: e.target.value })}
                  placeholder="Optional display name"
                />
              </div>
            )}

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">
                Address {config.requireAddress ? config.requiredFieldMarker : ''}
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => updateFormData({ address: e.target.value })}
                className={hasFieldError(errors, 'address') ? 'border-red-500' : ''}
              />
              {hasFieldError(errors, 'address') && (
                <p className="text-xs text-red-500">{getFieldError(errors, 'address')}</p>
              )}
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="country">
                Country {config.requireCountry ? config.requiredFieldMarker : ''}
              </Label>
              <Select
                value={formData.country}
                onValueChange={(value) => updateFormData({ country: value })}
              >
                <SelectTrigger
                  className={hasFieldError(errors, 'country') ? 'border-red-500' : ''}
                >
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasFieldError(errors, 'country') && (
                <p className="text-xs text-red-500">{getFieldError(errors, 'country')}</p>
              )}
            </div>

            {/* VAT Number */}
            {config.showVatDetails && (
              <div className="space-y-2">
                <Label htmlFor="vat_number">
                  VAT Number {config.requireVatNumber ? config.requiredFieldMarker : ''}
                </Label>
                <Input
                  id="vat_number"
                  value={formData.vat_number}
                  onChange={(e) => updateFormData({ vat_number: e.target.value })}
                  className={
                    hasFieldError(errors, 'vat_number') 
                      ? 'border-red-500' 
                      : formData.vat_number && formData.country && config.enableVatValidation
                        ? validateVatNumber(formData.vat_number, formData.country)
                          ? 'border-green-500' 
                          : 'border-orange-500'
                        : ''
                  }
                  placeholder="Optional"
                />
                {hasFieldError(errors, 'vat_number') && (
                  <p className="text-xs text-red-500">{getFieldError(errors, 'vat_number')}</p>
                )}
              </div>
            )}

            {/* VAT Setting */}
            {config.showVatDetails && (
              <div className="space-y-2">
                <Label htmlFor="vat_setting">VAT Setting</Label>
                <Select
                  value={formData.vat_setting}
                  onValueChange={(value) => 
                    updateFormData({ 
                      vat_setting: value as typeof formData.vat_setting,
                      // Reset override fields when changing from override
                      ...(value !== 'override' && {
                        vat_rate: 0,
                        vat_override_message: ''
                      })
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select VAT setting" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="reverse">Reverse Charge</SelectItem>
                    <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                    <SelectItem value="override">Override</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">
                Invoice Email {config.requiredFieldMarker}
              </Label>                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData({ email: e.target.value })}
                  className={hasFieldError(errors, 'email') ? 'border-red-500' : ''}
                />
              {hasFieldError(errors, 'email') && (
                <p className="text-xs text-red-500">{getFieldError(errors, 'email')}</p>
              )}
            </div>

            {/* VAT Override Fields */}
            {config.showVatDetails && formData.vat_setting === 'override' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vat_rate">
                    VAT Rate (%) {config.requiredFieldMarker}
                  </Label>
                  <Input
                    id="vat_rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.vat_rate}
                    onChange={(e) => updateFormData({ vat_rate: Number(e.target.value) })}
                    className={hasFieldError(errors, 'vat_rate') ? 'border-red-500' : ''}
                  />
                  {hasFieldError(errors, 'vat_rate') && (
                    <p className="text-xs text-red-500">{getFieldError(errors, 'vat_rate')}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vat_override_message">
                    Override Message {config.requiredFieldMarker}
                  </Label>
                  <Input
                    id="vat_override_message"
                    value={formData.vat_override_message}
                    onChange={(e) => updateFormData({ vat_override_message: e.target.value })}
                    className={hasFieldError(errors, 'vat_override_message') ? 'border-red-500' : ''}
                    placeholder="Custom VAT message for invoices"
                  />
                  {hasFieldError(errors, 'vat_override_message') && (
                    <p className="text-xs text-red-500">{getFieldError(errors, 'vat_override_message')}</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Peppol E-Invoicing Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="peppol_enabled" className="text-base font-medium">
                  Peppol E-Invoicing
                </Label>
                <p className="text-sm text-muted-foreground">
                  Send invoices electronically via the Peppol network
                </p>
              </div>
              <Switch
                id="peppol_enabled"
                checked={formData.peppol_enabled || false}
                onCheckedChange={async (checked) => {
                  const updates: any = { peppol_enabled: checked };
                  let peppolId = formData.peppol_id;
                  let peppolScheme = formData.peppol_scheme;
                  
                  // Auto-fill Peppol ID from VAT number if enabling and not set
                  if (checked && !formData.peppol_id && formData.vat_number) {
                    peppolId = formData.vat_number;
                    updates.peppol_id = peppolId;
                  }
                  // Auto-select scheme based on country
                  if (checked && !formData.peppol_scheme && formData.country) {
                    peppolScheme = getSchemeCodeForCountry(formData.country) ?? undefined;
                    if (peppolScheme) {
                      updates.peppol_scheme = peppolScheme;
                    }
                  }
                  
                  updateFormData(updates);
                  
                  // Auto-lookup if we have both ID and scheme
                  if (checked && (peppolId || formData.peppol_id) && (peppolScheme || formData.peppol_scheme)) {
                    lookupPeppolParticipant(
                      peppolId || formData.peppol_id || '',
                      peppolScheme || formData.peppol_scheme || ''
                    );
                  } else {
                    setPeppolLookupStatus('idle');
                    setPeppolLookupMessage('');
                  }
                }}
              />
            </div>
            
            {formData.peppol_enabled && (
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="peppol_id">Peppol ID (usually VAT number)</Label>
                    <Input
                      id="peppol_id"
                      value={formData.peppol_id || ''}
                      onChange={(e) => {
                        updateFormData({ peppol_id: e.target.value });
                        setPeppolLookupStatus('idle');
                      }}
                      placeholder="e.g., BE0123456789"
                      className={hasFieldError(errors, 'peppol_id') ? 'border-red-500' : ''}
                    />
                    {hasFieldError(errors, 'peppol_id') && (
                      <p className="text-xs text-red-500">{getFieldError(errors, 'peppol_id')}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="peppol_scheme">Peppol Scheme</Label>
                    <Select
                      value={formData.peppol_scheme || ''}
                      onValueChange={(value) => {
                        updateFormData({ peppol_scheme: value });
                        setPeppolLookupStatus('idle');
                      }}
                    >
                      <SelectTrigger className={hasFieldError(errors, 'peppol_scheme') ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select scheme" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PEPPOL_SCHEMES).map(([code, info]) => (
                          <SelectItem key={code} value={code}>
                            {info.name} ({code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasFieldError(errors, 'peppol_scheme') && (
                      <p className="text-xs text-red-500">{getFieldError(errors, 'peppol_scheme')}</p>
                    )}
                  </div>
                </div>
                
                {/* Peppol Lookup Button and Status */}
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.peppol_id && formData.peppol_scheme) {
                        lookupPeppolParticipant(formData.peppol_id, formData.peppol_scheme);
                      }
                    }}
                    disabled={!formData.peppol_id || !formData.peppol_scheme || peppolLookupStatus === 'loading'}
                  >
                    {peppolLookupStatus === 'loading' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Verify on Peppol
                  </Button>
                  
                  {peppolLookupStatus === 'found' && (
                    <Badge variant="default" className="bg-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {peppolLookupMessage}
                    </Badge>
                  )}
                  
                  {peppolLookupStatus === 'not_found' && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <XCircle className="h-3 w-3" />
                      {peppolLookupMessage}
                    </Badge>
                  )}
                  
                  {peppolLookupStatus === 'error' && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <XCircle className="h-3 w-3" />
                      {peppolLookupMessage}
                    </Badge>
                  )}
                </div>
                
                {peppolLookupStatus === 'not_found' && (
                  <Alert>
                    <AlertDescription className="text-sm">
                      This participant is not yet registered on the Peppol network. They may not be able to receive electronic invoices via Peppol.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          {/* Contact Persons */}
          <ContactPersonForm
            contacts={formData.contacts}
            onChange={(contacts) => updateFormData({ contacts })}
            errors={errors}
          />

          {/* Client Contacts */}
          <ClientContactsForm
            clientContacts={formData.client_contacts}
            onChange={(client_contacts) => updateFormData({ client_contacts })}
            availableUsers={users}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? 'Saving...' : client ? 'Update Client' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
